import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Check, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtError } from "@/lib/api";
import { L } from "@/constants/labels";

/**
 * Bandeau Source 3 — confirmation de la zone d'intervention.
 *
 * Affiché tant que ``geo_source != "declare"``. Pré-rempli avec les
 * valeurs déduites du SIRET (Source 1) ou du profil. Un clic « Confirmer »
 * bascule ``geo_source`` à ``declare`` et empêche tout batch ultérieur
 * d'écraser le choix de l'artisan.
 *
 * Design :
 * - Ambre pour la variante "à confirmer" (non bloquant, invite à
 *   l'action) — rouge réservé aux blocages critiques.
 * - Champ CP contrôlé, rayon 30 km par défaut, action inline + bouton
 *   « Plus tard » qui masque le bandeau **pour cette session seulement**
 *   (rappel à la prochaine connexion).
 */
export default function GeoConfirmBanner() {
  const { user } = useAuth();
  const [state, setState] = useState(null);   // {geo_source, code_postal, ville, rayon_km, ...}
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("geo_banner_dismissed") === "1"
  );
  const [cp, setCp] = useState("");
  const [ville, setVille] = useState("");
  const [rayon, setRayon] = useState(30);

  useEffect(() => {
    if (!user || user.role !== "prestataire") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/prestataires/me/geo");
        if (!alive) return;
        setState(data);
        setCp(data.code_postal || "");
        setVille(data.ville || "");
        setRayon(Number.isInteger(data.rayon_km) ? data.rayon_km : 30);
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user || user.role !== "prestataire") return null;
  if (!state || !state.needs_confirm || dismissed) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!/^\d{5}$/.test(cp)) {
      toast.error("Code postal invalide (5 chiffres attendus).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/prestataires/me/geo/confirm", {
        code_postal: cp, ville: ville || null, rayon_km: rayon,
      });
      toast.success("Zone d'intervention confirmée.");
      setState({ ...state, needs_confirm: false });
    } catch (err) { toast.error(fmtError(err)); }
    finally { setBusy(false); }
  };

  const later = () => {
    sessionStorage.setItem("geo_banner_dismissed", "1");
    setDismissed(true);
  };

  const contextMsg = state.lat && state.lng
    ? L.wording.geoZonePreremplie
    : L.wording.geoZoneManque;

  return (
    <div
      className="border-l-4 border-[#E8710A] bg-[#FFF7ED] p-3"
      data-testid="geo-confirm-banner"
    >
      <div className="flex items-start gap-3">
        <MapPin size={18} className="text-[#E8710A] mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-[#1F2933]">
            <strong>Confirmez votre zone d'intervention</strong> — {contextMsg}
          </div>
          <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-[#3E4C59]">
              <div>Code postal</div>
              <input
                type="text" inputMode="numeric" maxLength={5}
                value={cp} onChange={(e) => setCp(e.target.value.replace(/\D/g, ""))}
                className="input mt-0.5 w-24"
                placeholder="75009"
                data-testid="geo-banner-cp"
              />
            </label>
            <label className="text-xs text-[#3E4C59]">
              <div>Ville (facultatif)</div>
              <input
                type="text" maxLength={120}
                value={ville} onChange={(e) => setVille(e.target.value)}
                className="input mt-0.5 w-48"
                placeholder="Paris"
                data-testid="geo-banner-ville"
              />
            </label>
            <label className="text-xs text-[#3E4C59]">
              <div>Rayon (km)</div>
              <input
                type="number" min={0} max={500}
                value={rayon} onChange={(e) => setRayon(parseInt(e.target.value || "0", 10))}
                className="input mt-0.5 w-24"
                data-testid="geo-banner-rayon"
              />
            </label>
            <button
              type="submit" disabled={busy} className="btn-primary"
              data-testid="geo-banner-confirm"
            >
              <Check size={14} /> Confirmer
            </button>
            <button
              type="button" onClick={later} className="btn-ghost text-xs"
              data-testid="geo-banner-later"
            >
              <X size={12} /> Plus tard
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
